/**
 * Iter 52 — Elite Annotation overlay for the PDF proofing viewer.
 * Iter 55 — Multi-color palette (Orange default, Red, Green) applied to both
 *          pen and highlighter tools. Legacy strokes without a `color` field
 *          fall back to the original tool-based color (orange / yellow) so no
 *          historical annotation loses its look.
 *
 * A single <svg> layered above each rendered <Page>. Points are stored in
 * NORMALIZED [0..1] page coordinates so the same annotation replays perfectly
 * at any zoom level, on any device, in either the Admin modal or the Client
 * share route.
 *
 * When `interactive` is true, the layer captures pointer events and calls
 * onStrokeComplete(stroke) once the user releases. Otherwise it's a passive
 * playback surface (pointer-events: none) so text selection under the PDF
 * continues to work.
 */
import { useEffect, useRef, useState } from "react";

// Iter 55 — swatch palette. Pen is opaque, highlighter is translucent.
export const PALETTE = {
  orange: "var(--brand-accent)",
  red:    "#ef4444",
  green:  "#22c55e",
};

// Per-tool geometry (width) + opacity — independent of color.
const TOOL_GEOMETRY = {
  pen:       { width: 3,  opacity: 1 },
  highlight: { width: 14, opacity: 0.35 },
};

// Legacy fallback for strokes saved before the palette existed.
// Old highlighter strokes were yellow (#facc15), not palette-derived.
const LEGACY_COLORS = {
  pen: "var(--brand-accent)",
  highlight: "#facc15",
};

function resolveStyle(stroke) {
  const geo = TOOL_GEOMETRY[stroke.tool] || TOOL_GEOMETRY.pen;
  const color = stroke.color && PALETTE[stroke.color]
    ? PALETTE[stroke.color]
    : LEGACY_COLORS[stroke.tool] || LEGACY_COLORS.pen;
  return { stroke: color, width: geo.width, opacity: geo.opacity };
}

// Cheap point-list → smooth SVG path (Catmull-Rom-ish quadratic smoothing).
function toPath(points) {
  if (!points || points.length === 0) return "";
  if (points.length === 1) {
    const p = points[0];
    return `M ${p.x} ${p.y} L ${p.x + 0.001} ${p.y + 0.001}`;
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  return d;
}

export default function PdfAnnotationLayer({
  pageNumber,
  pageWidth,   // rendered pixel width of the page (react-pdf `width` prop)
  pageHeight,  // rendered pixel height (react-pdf reports via onLoadSuccess)
  strokes = [],           // Persistent strokes: [{ tool, color, points:[{x,y},…], id?, commentId? }]
  interactive = false,    // When true, capture new strokes
  activeTool = "pen",     // 'pen' | 'highlight'
  activeColor = "orange", // Iter 55 — palette swatch stamped onto new strokes
  onStrokeComplete,       // (stroke) => void
  highlightedCommentIds = [], // set of comment ids whose strokes should glow briefly
}) {
  const svgRef = useRef(null);
  const [drafting, setDrafting] = useState(null); // {tool, color, points:[{x,y},…]}

  // Convert a native pointer event's client coords to normalized [0..1] page space.
  const toNormalized = (ev) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (ev.clientY - rect.top) / rect.height));
    return { x, y };
  };

  const onPointerDown = (ev) => {
    if (!interactive) return;
    ev.preventDefault();
    ev.stopPropagation();
    svgRef.current.setPointerCapture(ev.pointerId);
    setDrafting({ tool: activeTool, color: activeColor, points: [toNormalized(ev)] });
  };

  const onPointerMove = (ev) => {
    if (!interactive || !drafting) return;
    // Coalesce to avoid explosive point count on high-refresh trackpads.
    const pt = toNormalized(ev);
    setDrafting((prev) => {
      if (!prev) return prev;
      const last = prev.points[prev.points.length - 1];
      if (last && Math.hypot(pt.x - last.x, pt.y - last.y) < 0.002) {
        return prev; // dedupe near-duplicate samples (page-relative epsilon)
      }
      return { ...prev, points: [...prev.points, pt] };
    });
  };

  const onPointerUp = (ev) => {
    if (!interactive || !drafting) return;
    try { svgRef.current.releasePointerCapture(ev.pointerId); } catch {}
    if (drafting.points.length >= 2) {
      onStrokeComplete?.(drafting);
    }
    setDrafting(null);
  };

  // Reset in-progress stroke if capture is disabled mid-draw.
  useEffect(() => {
    if (!interactive && drafting) setDrafting(null);
  }, [interactive]); // eslint-disable-line

  // The SVG uses a viewBox of 1x1 so we can push normalized [0..1] coords
  // straight in. `preserveAspectRatio="none"` maps 0..1 to the full page rect.
  return (
    <svg
      ref={svgRef}
      data-testid={`pdf-annotation-layer-${pageNumber}`}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        // Iter 52 — Z-index above react-pdf's text/annotation layers (which
        // sit at ~z-index 2). Otherwise the text layer intercepts pointerdown
        // as a text selection and we never get the draw event.
        zIndex: interactive ? 30 : 5,
        pointerEvents: interactive ? "auto" : "none",
        cursor: interactive ? "crosshair" : "default",
        touchAction: interactive ? "none" : "auto",
      }}
    >
      {/* Persistent strokes — playback */}
      {strokes.map((s, idx) => {
        const style = resolveStyle(s);
        const glow = s.commentId && highlightedCommentIds.includes(s.commentId);
        return (
          <path
            key={s.id || `${idx}-${s.tool}`}
            data-testid={s.commentId ? `annotation-stroke-${s.commentId}` : undefined}
            d={toPath(s.points)}
            fill="none"
            stroke={style.stroke}
            // With `vectorEffect="non-scaling-stroke"` the stroke width is in
            // user (device) pixels, not viewBox units, so we pass raw px.
            strokeWidth={style.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity={style.opacity}
            vectorEffect="non-scaling-stroke"
            style={glow ? {
              filter: `drop-shadow(0 0 6px ${style.stroke}) drop-shadow(0 0 10px ${style.stroke})`,
              transition: "filter 200ms ease-out",
            } : undefined}
          />
        );
      })}
      {/* In-progress draft — same styling as final playback */}
      {drafting && (() => {
        const style = resolveStyle(drafting);
        return (
          <path
            d={toPath(drafting.points)}
            fill="none"
            stroke={style.stroke}
            strokeWidth={style.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity={style.opacity}
            vectorEffect="non-scaling-stroke"
          />
        );
      })()}
    </svg>
  );
}
